import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import './JobMatchDetail.scss';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import CellIndicator from 'ps-components/LoadingIndicator/CellIndicator/CellIndicator';
import { JMDProps, JMDTableProps } from './types/JobMatchDetailTypes';
import { jobMatchGridActions } from './JobMatchGridCreate';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable.js'

/** JobMatchDetail component rendered in LandingPageGridCreate as a default left column */
export const JobMatchDetail: React.FC<JMDProps> = (props): JSX.Element => {
  const { rowData } = props;
  const location = useLocation();
  const matchCount = rowData?.match_count_default;
  const orgJobId = rowData?.org_job_id_default;
  const jobTitle = rowData?.field_org_job_title;
  const jobCode = rowData?.field_org_job_code || rowData?.org_job_code_default;
  const jobCodeKey = rowData?.field_org_job_code_key || rowData?.org_job_code_key_default;
  const currency = rowData?.field_currency;
  const isSlotted = rowData?.field_slot_p === 'Slotted' ? true : false;
  const disabled = !matchCount;
  const viewName = undefined;
  const job = {
    title: jobTitle,
    id: orgJobId,
    code: jobCode,
    codeKey: jobCodeKey
  };

  const isExpanded: boolean | undefined = useSelector((state: any) => state.landingPage.jobMatchGridReducer.jobMatchExpandedSections[orgJobId]);
  const benchmarkYear: number = useSelector((state: any) => state.aging.benchmarkYear);
  const masterAccountId: string = useSelector((state: any) => state.session.organization.masterAccountId);
  const displayIn: string = useSelector((state: any) => state.marketPricing.MarketPricingReducer.displayIn);
  const currentJobExists: boolean = useSelector((state: any) => state.landingPage.jobMatchGridReducer.jobMatchTableProps.hasOwnProperty(orgJobId));
  const currentJobIsStale: boolean | undefined = useSelector((state: any) => state.landingPage.jobMatchGridReducer.jobMatchTableProps[orgJobId]?.isStale);
  const slideoutOpen: boolean = useSelector((state: any) => state.slideout.open);
  const loadingStatus: boolean = useSelector((state: any) => state.landingPage.jobMatchGridReducer.jobMatchDetailsLoading.includes(orgJobId));

  const dispatch = useDispatch();
  const { getJobMatchDetails, toggleJobMatchExpandedSection } = jobMatchGridActions;
  const [isLoading, setIsLoading] = useState(false);

  const fetchJobMatchDetails = () =>
    dispatch(getJobMatchDetails(job, benchmarkYear, displayIn, masterAccountId, currency, isExpanded, viewName, isSlotted));

  const handleClick = () => {
    if (disabled) return;
    if (!currentJobExists || currentJobIsStale) {
      fetchJobMatchDetails();
    }
    dispatch(toggleJobMatchExpandedSection(orgJobId, !isExpanded));
  };

  useEffect(() => {
    if (currentJobIsStale && !slideoutOpen && location.pathname?.includes('/market-analysis/market-pricing'))
      fetchJobMatchDetails();
  }, [currentJobIsStale, slideoutOpen, location.pathname]);

  useEffect(() => {
    setIsLoading(loadingStatus);
  });

  return (
    <div className="job-match-detail-container" onClick={handleClick}>
      {isLoading ? (
        <CellIndicator iconClass="icon-animate-spin icon-spin cell-indicator" />
      ) : (
        <i className={classnames(isExpanded ? 'icon-down-open' : 'icon-up-open', disabled && 'disabled')}></i>
      )}
      <span className={classnames('number-of-matches', disabled && 'disabled')}>{matchCount || 0}</span>
    </div>
  );
}

/** renders the advanced tables that contain job matches on the landing page, used in AdvancedTable*/
export const getJobMatches =
  (orgJobId: number,
  jobMatchDetailsLoading: number[],
  jobMatchExpandedSections: Record<string | number, boolean>,
  jobMatchTableProps: Record<string | number, JMDTableProps>,
  organizationCode: string) => {

  if (
    orgJobId &&
    !jobMatchDetailsLoading.includes(orgJobId) &&
    jobMatchExpandedSections[orgJobId] &&
    jobMatchTableProps &&
    jobMatchTableProps[orgJobId]?.rowData?.length > 0
  ) {
    const tableProps = jobMatchTableProps[orgJobId];

    return (
      <div className="job-match-grid-container">
        <AdvancedTable {...tableProps} organizationCode={organizationCode} />
      </div>
    );
  }
  return [];
};
